import { Button, useMounted } from 'src/design-system'
import { observer } from 'mobx-react-lite'
import { ZodError } from 'zod'
import * as React from 'react'
import { Editor, EditorContext } from 'components/atoms/editor'
import { ProfileSkillSelect } from 'components/profile-skill-select'
import { SkillSelect } from 'components/skill-select'
import { UserSelect } from 'components/user-select'
import { useStore } from 'store/context'
import { VisibilitySelect } from 'components/visibility-select'
import { UploadAttachmentsButton } from 'components/attachments/upload-attachments'
import { WinFormProps } from './win-form.types'
import { WinFormVm } from './win-form-vm'
import { uploadAttachments } from 'components/attachments/upload-attachment-vm'
import { errorToast } from 'app/packs/src/utils/error-toast'
import { validateFiles } from 'src/utils/file-validation'

export const WinForm = observer((props: WinFormProps) => {
  const {
    autoFocus,
    category = 'Win',
    initialContent,
    initialSkillIds,
    initialUserIds,
    onSuccessfulSubmit,
    setContent,
    source,
    winId,
  } = props

  const { currentUser, skills, users, userSkills, wins, profileSkills, debug } =
    useStore()

  const win = winId ? wins.byId(winId) : undefined

  let defaultUserIds = React.useMemo(
    () => initialUserIds || [],
    [initialUserIds]
  )
  if (category === 'Note' && currentUser) defaultUserIds = [currentUser.id]
  if (win) defaultUserIds = win.winnerIds

  const [userIds, setUserIds] = React.useState<string[]>(defaultUserIds)
  const [submitCount, setSubmitCount] = React.useState(0)
  const [attachments, setAttachments] = React.useState<File[]>([])
  const [uploadingFiles, setUploadingFiles] = React.useState<boolean>(false)
  const editor = React.useRef<EditorContext>(null)
  const mounted = useMounted()

  const handleUserIdsChange = React.useCallback(
    (newUserIds: string[]) => {
      if (category === 'Note' && currentUser) {
        if (!newUserIds.includes(currentUser.id)) {
          newUserIds = [currentUser.id, ...newUserIds]
        }
      }
      setUserIds(newUserIds)
    },
    [category, currentUser]
  )

  const handleFileSelect = React.useCallback(
    (files: FileList) => {
      const fileArray = Array.from(files)
      const existingAttachmentCount =
        (win?.attachmentData?.length || 0) + attachments.length

      const validation = validateFiles(fileArray, {
        maxFiles: 5,
        currentFileCount: existingAttachmentCount,
      })

      if (validation.errors.length > 0) {
        validation.errors.forEach((error) => {
          errorToast(error)
        })

        if (validation.validFiles.length > 0) {
          setAttachments((prev) => [...prev, ...validation.validFiles])
        }
        return
      }

      setAttachments((prev) => [...prev, ...validation.validFiles])
      setFormError(null)
    },
    [win?.attachmentData?.length, attachments.length]
  )

  const removeAttachment = React.useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const vm = React.useMemo(() => {
    if (!currentUser) return

    return new WinFormVm(
      currentUser,
      userIds,
      category,
      source,
      initialContent,
      initialSkillIds,
      win
    )
  }, [
    userIds,
    currentUser,
    category,
    source,
    initialContent,
    initialSkillIds,
    win,
  ])

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const onSubmit = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (!vm) {
        return
      }

      const form = e.currentTarget
      if (!form || !(form instanceof HTMLFormElement)) {
        setFormError('Invalid form element')
        return
      }
      setIsSubmitting(true)

      try {
        let attachmentBlobIds: string[] = []
        if (attachments.length > 0) {
          setUploadingFiles(true)
          attachmentBlobIds = await uploadAttachments(attachments)
          setUploadingFiles(false)
        }

        await vm.onSubmit(form, attachmentBlobIds)
        setSubmitCount(submitCount + 1)
        if (!win) editor.current?.reset()
        onSuccessfulSubmit?.()
        setUserIds([])
        setAttachments([])
      } catch (errors) {
        if (errors instanceof ZodError) {
          setFormError('Please enter content.')
        } else {
          const errorMessage =
            errors instanceof Error
              ? errors.message
              : 'Something went wrong. Please try again.'
          if (
            !errorMessage.includes('File size') &&
            !errorMessage.includes('File format is not supported')
          ) {
            setFormError('Something went wrong. Please try again.')
          }
        }
      } finally {
        if (mounted.current) {
          setIsSubmitting(false)
          setUploadingFiles(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSuccessfulSubmit, submitCount, vm, debug, mounted, attachments, win]
  )

  React.useEffect(() => {
    async function fetchUserSkills() {
      await userSkills.fetchForUsers(userIds)
    }

    fetchUserSkills()
  }, [userIds, userSkills])

  const forCurrentUser = userIds.length === 1 && userIds[0] === currentUser?.id

  const focusSkillIds = forCurrentUser
    ? userSkills.focusedSkillIdsForUser(currentUser.id)
    : []

  const skillsForSelect = forCurrentUser
    ? currentUser.sortedSkills()
    : skills.allForUsersViaUserSkills(...userIds)

  if (win && userSkills.loading) return null

  if (!vm) return null

  return (
    <form
      className="pb-6 px-6"
      onSubmit={onSubmit}
      id="win-form"
      key={`win-form-${win?.id}-${submitCount}`}
    >
      <UserSelect
        className="mb-2.5 -mt-2"
        value={userIds}
        isDisabled={!!win}
        isMulti
        key={`user-select-${submitCount}`}
        onChange={handleUserIdsChange}
        prefix="For"
        required
        users={users.withPackage('grow')}
      />
      <Editor
        autoFocus={autoFocus}
        ref={editor}
        initialContent={vm.defaultContentValue}
        onChange={setContent}
        name="content"
        rows={4}
        placeholder={vm.placeholderText}
        className="-mx-2 mb-4"
      />
      {category === 'Note' &&
        (attachments.length > 0 ||
          (win?.attachmentData && win.attachmentData.length > 0)) && (
          <div className="mb-4 px-1">
            <div className="flex flex-wrap gap-2">
              {win?.attachmentData &&
                win.attachmentData.map((attachment, index) => (
                  <div
                    key={`existing-${attachment.id || index}`}
                    className="inline-flex items-center bg-blue-100 text-blue-600 px-1 py-0.3 text-xs rounded-sm"
                  >
                    <span
                      className="truncate max-w-48 font-medium"
                      title={attachment.filename}
                    >
                      {attachment.filename}
                    </span>
                  </div>
                ))}
              {attachments.map((file, index) => (
                <div
                  key={`new-${index}`}
                  className="inline-flex items-center bg-gray-100 text-gray-600 px-1 py-0.3 text-xs rounded-sm"
                >
                  <span
                    className="truncate max-w-48 font-medium"
                    title={file.name}
                  >
                    {file.name}
                  </span>
                  <span className="ml-1 text-gray-500 text-xs"></span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="ml-1 text-gray-600 hover:text-gray-800 text-lg leading-none"
                    title="Remove attachment"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      <div className="flex flex-col gap-y-2 sm:flex-row items-start sm:justify-between">
        <div className="flex flex-row items-center gap-x-2 flex-wrap gap-y-1">
          <SkillSelect
            key={`skill-select-${submitCount}`}
            skills={skillsForSelect}
            defaultSkillIds={vm.defaultSkillIds}
            focusSkillIds={focusSkillIds}
            noOptionsMessage={
              skillsForSelect.length === 0
                ? userIds.length > 0
                  ? 'No skills found'
                  : 'Select a user first'
                : undefined
            }
            source={source}
          />
          {forCurrentUser && profileSkills && (
            <ProfileSkillSelect
              key={`profile-skill-select-${submitCount}`}
              profileSkills={profileSkills.forUser(currentUser.id)}
              defaultProfileSkillIds={win?.profileSkillIds || []}
            />
          )}
          <VisibilitySelect
            key={`visibility-select-${submitCount}`}
            isForCurrentUser={vm.isForCurrentUser}
            initialStatus={vm.defaultVisibilityStatus}
            includedStatuses={vm.visibilityStatuses}
            isNote={category === 'Note'}
          />
        </div>
        <div className="flex justify-end w-full sm:w-auto gap-2">
          {category === 'Note' && (
            <UploadAttachmentsButton
              onFileSelect={handleFileSelect}
              disabled={isSubmitting || uploadingFiles}
            />
          )}
          <Button disabled={isSubmitting || uploadingFiles} type="submit">
            {uploadingFiles ? 'Uploading...' : vm.buttonText}
          </Button>
        </div>
      </div>
      {formError && <div className="mt-1 text-red-600">{formError}</div>}
    </form>
  )
})
